import { type FC } from "react";
import { Button, Input, ListBox, Select, TextField } from "@heroui/react";
import type { HelpExecutor, HelpExecutorProfile } from "../types";
import { emptyExecutor, executorFromProfile } from "../types";
import {
  MAX_EXECUTOR_EXPERIENCE_YEARS,
  clampExperienceYears,
  clampExecutorLat,
  clampExecutorLon,
} from "../helpMappers";
import HelpPhoneField from "./HelpPhoneField";

const NEW_EXECUTOR_KEY = "__new__";

interface HelpExecutorEditorProps {
  executor: HelpExecutor;
  roster: HelpExecutorProfile[];
  onChange: (executor: HelpExecutor) => void;
  onClear: () => void;
  embedded?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
}

const HelpExecutorEditor: FC<HelpExecutorEditorProps> = ({
  executor,
  roster,
  onChange,
  onClear,
  embedded = false,
  onSave,
  onCancel,
}) => {
  const enabledRoster = roster.filter((item) => item.enabled);
  const isManual = executor.source === "custom";
  const selectedKey = executor.catalogUuid ?? (isManual ? NEW_EXECUTOR_KEY : undefined);

  const set = (patch: Partial<HelpExecutor>) => {
    onChange({ ...executor, ...patch, source: "custom", catalogUuid: null });
  };

  const handleSelect = (key: string) => {
    if (key === NEW_EXECUTOR_KEY) {
      onChange(
        emptyExecutor({
          lat: executor.lat,
          lon: executor.lon,
          etaMinutes: executor.etaMinutes,
        }),
      );
      return;
    }
    const profile = roster.find((item) => item.uuid === key);
    if (!profile) return;
    onChange(
      executorFromProfile(profile, {
        lat: executor.lat,
        lon: executor.lon,
        etaMinutes: executor.etaMinutes,
      }),
    );
  };

  return (
    <div className="help-edit">
      <div className="help-executor-edit">
        <label className="help-form-field">
          Из списка
          <Select
            placeholder="Выбрать исполнителя"
            aria-label="Выбрать исполнителя из списка"
            selectedKey={selectedKey ?? null}
            onSelectionChange={(key) => {
              if (!key) return;
              handleSelect(String(key));
            }}
          >
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                <ListBox.Item id={NEW_EXECUTOR_KEY} textValue="Новый">
                  Новый
                </ListBox.Item>
                {enabledRoster.map((item) => (
                  <ListBox.Item
                    key={item.uuid}
                    id={item.uuid}
                    textValue={item.name}
                  >
                    {item.name} · {item.vehiclePlate || item.phone}
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        </label>

        {executor.catalogUuid && executor.name ? (
          <div className="help-executor-preview">
            <strong>{executor.name}</strong>
            <span>
              {[
                executor.phone,
                executor.vehicleModel,
                executor.vehiclePlate,
                executor.company,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        ) : null}

        {isManual ? (
          <div className="help-executor-manual">
            <span className="help-executor-manual__label">Вручную</span>
            <div className="help-edit-grid">
              <label className="help-form-field">
                Имя *
                <TextField fullWidth>
                  <Input
                    value={executor.name}
                    onChange={(e) => set({ name: e.target.value })}
                    placeholder="Имя специалиста"
                  />
                </TextField>
              </label>
              <label className="help-form-field">
                Телефон *
                <HelpPhoneField
                  value={executor.phone}
                  onChange={(phone) => set({ phone })}
                />
              </label>
              <label className="help-form-field">
                Авто
                <TextField fullWidth>
                  <Input
                    value={executor.vehicleModel}
                    onChange={(e) => set({ vehicleModel: e.target.value })}
                  />
                </TextField>
              </label>
              <label className="help-form-field">
                Госномер
                <TextField fullWidth>
                  <Input
                    value={executor.vehiclePlate}
                    onChange={(e) => set({ vehiclePlate: e.target.value })}
                  />
                </TextField>
              </label>
              <label className="help-form-field">
                Компания
                <TextField fullWidth>
                  <Input
                    value={executor.company}
                    onChange={(e) => set({ company: e.target.value })}
                  />
                </TextField>
              </label>
              <label className="help-form-field">
                Стаж, лет
                <TextField fullWidth>
                  <Input
                    inputMode="numeric"
                    value={String(executor.experienceYears || "")}
                    onChange={(e) =>
                      set({
                        experienceYears: clampExperienceYears(
                          Number(e.target.value) || 0,
                        ),
                      })
                    }
                    placeholder={`0–${MAX_EXECUTOR_EXPERIENCE_YEARS}`}
                  />
                </TextField>
              </label>
              <label className="help-form-field">
                ETA, мин
                <TextField fullWidth>
                  <Input
                    inputMode="numeric"
                    value={
                      executor.etaMinutes == null ? "" : String(executor.etaMinutes)
                    }
                    onChange={(e) =>
                      set({
                        etaMinutes:
                          e.target.value === "" ? null : Number(e.target.value) || 0,
                      })
                    }
                  />
                </TextField>
              </label>
              <label className="help-form-field">
                Широта
                <TextField fullWidth>
                  <Input
                    inputMode="decimal"
                    placeholder="−90…90"
                    value={executor.lat == null ? "" : String(executor.lat)}
                    onChange={(e) =>
                      set({
                        lat:
                          e.target.value === ""
                            ? null
                            : clampExecutorLat(Number(e.target.value) || 0),
                      })
                    }
                  />
                </TextField>
              </label>
              <label className="help-form-field help-form-field--wide">
                Долгота
                <TextField fullWidth>
                  <Input
                    inputMode="decimal"
                    placeholder="−180…180"
                    value={executor.lon == null ? "" : String(executor.lon)}
                    onChange={(e) =>
                      set({
                        lon:
                          e.target.value === ""
                            ? null
                            : clampExecutorLon(Number(e.target.value) || 0),
                      })
                    }
                  />
                </TextField>
              </label>
            </div>
            <p className="help-executor-geo__hint">
              Важно! Обязательно указывайте оба поля — «Широта» и «Долгота». Если
              заполнено только одно значение, координаты не сохранятся.
            </p>
          </div>
        ) : null}
      </div>

      <div className="help-edit-actions">
        <Button size="sm" variant="ghost" onPress={onClear}>
          Снять исполнителя
        </Button>
        {!embedded ? (
          <div className="help-edit-actions__end">
            <Button size="sm" variant="tertiary" onPress={onCancel}>
              Отмена
            </Button>
            <Button size="sm" variant="primary" onPress={onSave}>
              Сохранить
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HelpExecutorEditor;
